import 'C:\\Projects\\genkei\\zumeco\\static\\shared\\utils.ts.esbuild-plugin-windicss.css';
import { __ACCOUNTS_URL_BASE__ } from '@@urls';
import comparatorFactoryFactory from 'comparator-factory-factory';
import type { PageNumbers } from '../../server/src/models';
import { i18n } from './i18n';
export const supportedImageExtensions = '.apng,.avif,.bmp,.gif,.ico,.jpg,.jpeg,.jpe,.jif,.jfif,.png,.svg,.webp';
export const supportedDrawingExtensions = `.pdf,.dxf,${supportedImageExtensions}`;
export const isAcceptedFile = (file: File, acceptedContentTypeOrExtension: readonly string[]) => acceptedContentTypeOrExtension.some(a => a.startsWith('.') ? file.name.toLowerCase().endsWith(a) : a.endsWith('/*') ? file.type.startsWith(a.slice(0, -1)) : file.type === a);

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const noop = () => {};
export const isNonNull = <T,>(value: T): value is NonNullable<T> => value !== undefined && value !== null;
export const clamp = (value: number, min: number, max: number) => value < min ? min : value > max ? max : value;
export function* reverseArray<T>(array: readonly T[]): Generator<T> {
  for (let i = array.length - 1; i >= 0; i--) {
    yield array[i];
  }
}
export const debounce = (fn: () => void, delay: number) => {
  let handle: number | undefined;
  const wrappedFn = () => (handle = undefined, fn());
  return () => (clearTimeout(handle), handle = setTimeout(wrappedFn, delay));
};
export const debounceAnimationFrame = (fn: () => void) => {
  let handle: number | undefined;
  const wrappedFn = () => (handle = undefined, fn());
  return () => (handle !== undefined && cancelAnimationFrame(handle), handle = requestAnimationFrame(wrappedFn));
};
export const isSafari = 'GestureEvent' in self;
export const isMobileSafari = isSafari && 'ontouchend' in self;
export const isSafariBelow27 = isSafari && 'nearestViewportElement' in SVGGraphicsElement.prototype;
export const preventDefault = (event: Event) => event.preventDefault();
export const nameAndEmail = ({
  email,
  name
}: {
  readonly email: string;
  readonly name?: string;
}) => name ? `${name} <${email}>` : email;
export const namesAndEmails = (users: readonly {
  readonly email: string;
  readonly name?: string;
}[]) => users.map(nameAndEmail).join(',\n');
export const isIgnorableErrorEvent = (error: ErrorEvent) => error.message === 'Uncaught EvalError: Possible side-effect in debug-evaluate' || error.message === 'Uncaught SyntaxError: Invalid or unexpected token' || error.message === 'Uncaught SyntaxError: Unexpected end of input';
export const deleteControlCharacters = (text: string) => text.replace(/\p{gc=Cc}/gu, c => c === '\n' || c === '\r' || c === '\t' ? c : ' ');
export const loginUrl = () => `${__ACCOUNTS_URL_BASE__}/login/?continue=${encodeURIComponent(location.href)}`;
export const comparing = comparatorFactoryFactory({
  collator: {
    numeric: true
  }
});
export const range = (start: number, end: number) => {
  const result: number[] = [];
  for (let i = start; i < end; i++) {
    result.push(i);
  }
  return result;
};
export const focusAndSelectAll = (element: HTMLInputElement | null) => {
  setTimeout(() => {
    if (element) {
      element.focus();
      element.select();
    }
  });
};
export const parsePageNumbers = (s: string): PageNumbers => {
  const result: (number | [number, number])[] = [];
  for (const segment of s.normalize('NFKC').replace(/\s*/g, '').split(/[,，､、]/)) {
    const rangeMatch = segment.match(/^([0-9]+)[-−–ー~〜]([0-9]+)$/);
    if (rangeMatch) {
      result.push([parseInt(rangeMatch[1], 10), parseInt(rangeMatch[2], 10)]);
      continue;
    }
    const pageNumber = parseInt(segment, 10);
    if (pageNumber) {
      result.push(pageNumber);
    }
  }
  return result;
};
export const extractPageNumbers = (pageNumbers: PageNumbers | undefined, totalPageCount: number) => pageNumbers?.flatMap(p => typeof p === 'number' ? [p] : range(p[0], p[1] + 1)) ?? range(1, totalPageCount + 1);
export const defaultResponseHandler = async (response: Response, notify = alert) => {
  if (response.ok) {
    return response;
  }
  const json = (await response.json().catch(noop)) as {
    message?: string;
    code?: string;
  } | undefined;
  if (json?.message) {
    return notify(json.message);
  }
  if (json?.code) {
    return notify(i18n.errorWithCode(json.code));
  }
  switch (response.status) {
    case 401:
      alert(i18n.pleaseLogin);
      location.href = loginUrl();
      return;
    case 402:
      notify(i18n.paymentRequired);
      return;
    case 403:
      notify(i18n.permissionDenied);
      return;
    case 404:
      notify(i18n.notFound);
      return;
    default:
      notify(i18n.errorWithStatus(response.status));
      return;
  }
};
export const withDefaultErrorHandler = async (response: Promise<Response>, notify = alert) => {
  try {
    return await defaultResponseHandler(await response, notify);
  } catch {
    alert(i18n.networkErrorOccurred);
  }
};
const sendJson = (url: string, method: string, body: unknown) => fetch(url, {
  method,
  headers: {
    'Content-Type': 'application/json; charset=utf-8'
  },
  body: JSON.stringify(body)
});
export const postJson = <Body = unknown,>(url: string, body: Body) => sendJson(url, 'POST', body);
export const patchJson = <Body = unknown,>(url: string, body: Body) => sendJson(url, 'PATCH', body);
export const withProgressBar = async <T,>(callback: () => Promise<T>) => {
  try {
    topbar.show();
    return await callback();
  } finally {
    topbar.hide();
  }
};
export const createCanvas = ({
  width,
  height
}: {
  readonly width: number;
  readonly height: number;
}) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
};
export const loadImage = (url: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();
  // Safari では load イベントが来た段階ではまだ読み込みが終わっていない可能性があります。正しい対処法は不明です。
  // 参考: https://stackoverflow.com/questions/69949555/convert-svg-with-image-not-working-in-safari
  image.onload = isSafari ? () => setTimeout(() => resolve(image), 300) : () => resolve(image);
  image.onerror = (event, _source, _lineno, _colno, error = Error(`Could not load image: ${url}`)) => {
    console.error('Could not load image:', url, event, error);
    reject(error);
  };
  image.src = url;
});
export const loadImageBlob = async (blob: Blob) => {
  if (blob.type === 'image/svg+xml') {
    const url = URL.createObjectURL(blob);
    const image = await loadImage(url);
    URL.revokeObjectURL(url);
    return image;
  } else {
    return createImageBitmap(blob);
  }
};
export const blobToDataUrl = (blob: Blob) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});
export const imageSourceToDataUrl = async (uri: string) => {
  const response = await fetch(uri);
  const blob = await response.blob();
  return await blobToDataUrl(blob);
};
export const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) => new Promise<Blob | null>(resolve => canvas.toBlob(resolve, type, quality)).then(blob => {
  if (blob === null) {
    console.error('canvas.toBlob() returned null', {
      canvas,
      type,
      quality,
      width: canvas.width,
      height: canvas.height
    });
    throw new ReferenceError('canvas.toBlob() returned null');
  }
  return blob;
});
export const canvasToFile = async (canvas: HTMLCanvasElement, filename: string, type: string, quality?: number) => new File([await canvasToBlob(canvas, type, quality)], filename, {
  type
});
export const downloadFile = (filename: string, url: string) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
export const drawCanvasFooter = (canvas: HTMLCanvasElement, text: string, fontSize: number) => {
  const context = canvas.getContext('2d')!;
  context.fillStyle = 'white';
  context.font = `${fontSize}px sans-serif`;
  context.textAlign = 'left';
  context.textBaseline = 'bottom';
  context.fillText(text, 8, canvas.height - 8 - fontSize / 8);
  context.fillStyle = 'black';
  context.fillText(text, 8 + fontSize / 8, canvas.height - 8);
};
export const mapValues = <Key extends keyof any, SourceValue, DestinationValue>(source: Record<Key, SourceValue>, selector: (value: SourceValue, key: Key) => DestinationValue): Record<Key, DestinationValue> => {
  const result = {} as Record<Key, DestinationValue>;
  for (const [key, value] of Object.entries(source) as [Key, SourceValue][]) {
    result[key] = selector(value, key);
  }
  return result;
};
export function* childElements(parentElement: Element): Generator<Element> {
  for (let e = parentElement.firstElementChild; e; e = e.nextElementSibling) yield e;
}